/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { PatientRelationshipType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface EscortRequestProps {
  id: string;
  escortName: string;
  relationship: PatientRelationshipType;
  escortWeightInKg: number;
  requestApprovalTime: Date;
  requestId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class EscortRequestRecord extends Record<EscortRequestProps>({
  id: null as any,
  escortName: null as any,
  relationship: null as any,
  escortWeightInKg: null as any,
  requestApprovalTime: null as any,
  requestId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    escortName: string;
    relationship: PatientRelationshipType;
    escortWeightInKg: number;
    requestApprovalTime: string | number | Date;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): EscortRequestRecord {
    if (json == null) {
      throw new Error('EscortRequestRecord.fromJS: parameter cannot be null');
    }
    return new EscortRequestRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'EscortRequestJson.id',
      ),
      escortName: getFromOrThrow(json, (j) => j.escortName, 'EscortRequestJson.escortName'),
      relationship: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'relationship', PatientRelationshipType.Unspecified),
        'EscortRequestJson.relationship',
      ),
      escortWeightInKg: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'escortWeightInKg', 0.0),
        'EscortRequestJson.escortWeightInKg',
      ),
      requestApprovalTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'requestApprovalTime', '0001-01-01T00:00:00.00Z'),
          'EscortRequestJson.requestApprovalTime',
        ),
      ),
      requestId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
        'EscortRequestJson.requestId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'EscortRequestJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    escortName: string;
    relationship: PatientRelationshipType;
    escortWeightInKg: number;
    requestApprovalTime: string | number | Date;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [EscortRequestRecord, Error[]] {
    if (json == null) {
      throw new Error('EscortRequestRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new EscortRequestRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'EscortRequestJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      escortName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.escortName, 'EscortRequestJson.escortName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      relationship: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'relationship', PatientRelationshipType.Unspecified),
            'EscortRequestJson.relationship',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      escortWeightInKg: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'escortWeightInKg', 0.0),
            'EscortRequestJson.escortWeightInKg',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestApprovalTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'requestApprovalTime', '0001-01-01T00:00:00.00Z'),
              'EscortRequestJson.requestApprovalTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
            'EscortRequestJson.requestId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'EscortRequestJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: EscortRequestProps) {
    super(props);
    if (props == null) {
      throw new Error('EscortRequestRecord constructor parameter cannot be null');
    }
  }
}

export abstract class EscortRequestViewModelBase implements EscortRequestProps {
  protected readonly _props: EscortRequestProps;
  private _json?: EscortRequestProps;

  get id(): string {
    return this._props.id;
  }
  get escortName(): string {
    return this._props.escortName;
  }
  get relationship(): PatientRelationshipType {
    return this._props.relationship;
  }
  get escortWeightInKg(): number {
    return this._props.escortWeightInKg;
  }
  get requestApprovalTime(): Date {
    return this._props.requestApprovalTime;
  }
  get requestId(): string {
    return this._props.requestId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isOther() {
    return this._props.relationship === PatientRelationshipType.Other;
  }
  public get isParentOrGuardian() {
    return this._props.relationship === PatientRelationshipType.ParentOrGuardian;
  }
  public get isSpouse() {
    return this._props.relationship === PatientRelationshipType.Spouse;
  }
  public get isUnspecified() {
    return this._props.relationship === PatientRelationshipType.Unspecified;
  }

  public isAnyRelationship(...patientRelationshipType: PatientRelationshipType[]) {
    return patientRelationshipType.includes(this._props.relationship);
  }

  constructor(props: EscortRequestProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        escortName: this._props.escortName,
        relationship: this._props.relationship,
        escortWeightInKg: this._props.escortWeightInKg,
        requestApprovalTime: this._props.requestApprovalTime,
        requestId: this._props.requestId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllEscortRequestEntities = (state: AppState) =>
  state.entityStores.escortRequest.entities;

export const selectEscortRequestById = (id: string) => (state: AppState) =>
  state.entityStores.escortRequest.entities.get(id);

export const selectEscortRequestMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.escortRequest.meta.get(id);

export const selectEscortRequestAndMetadataById = (id: string) =>
  createSelector(
    selectEscortRequestById(id),
    selectEscortRequestMetadataById(id),
    (escortRequest, meta) => ({ escortRequest, meta }),
  );

export function selectEscortRequestEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllEscortRequestEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, EscortRequestRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectEscortRequestEntitiesByEscortName(escortName: string) {
  return createSelector(selectAllEscortRequestEntities, (entities) =>
    entities.filter((e) => e.escortName === escortName).valueSeq(),
  );
}

export function selectEscortRequestEntitiesByRelationship(relationship: PatientRelationshipType) {
  return createSelector(selectAllEscortRequestEntities, (entities) =>
    entities.filter((e) => e.relationship === relationship).valueSeq(),
  );
}

export function selectEscortRequestEntitiesByEscortWeightInKg(escortWeightInKg: number) {
  return createSelector(selectAllEscortRequestEntities, (entities) =>
    entities.filter((e) => e.escortWeightInKg === escortWeightInKg).valueSeq(),
  );
}

export function selectEscortRequestEntitiesByRequestApprovalTime(requestApprovalTime: Date) {
  return createSelector(selectAllEscortRequestEntities, (entities) =>
    entities.filter((e) => e.requestApprovalTime === requestApprovalTime).valueSeq(),
  );
}

export function selectEscortRequestEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllEscortRequestEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectEscortRequestEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllEscortRequestEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
